import { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import PageTransition from "@/components/PageTransition";
import { LayoutDashboard, Users, Truck, Route, Receipt, AlertTriangle, Bell, FileCheck, Wallet, History, BarChart3, Settings, LogOut, Menu, Search, X, Banknote } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Logo } from "@/components/common";
import { useDebounced } from "@/pages/admin/ui";
import PasswordRequests from "@/components/PasswordRequests";

const NAV = [
  ["/admin", "لوحة التحكم", LayoutDashboard, null, "dashboard"],
  ["/admin/drivers", "ملفات السائقين", Users, null, "drivers"],
  ["/admin/vehicles", "الشاحنات", Truck, null, "vehicles"],
  ["/admin/trips", "النقلات", Route, "trips_view", "trips"],
  ["/admin/invoices", "الفواتير", Receipt, "invoices_view", "invoices"],
  ["/admin/violations", "المخالفات", AlertTriangle, "violations_manage", "violations"],
  ["/admin/notifications", "الإشعارات", Bell, "notifications_send", "notifications"],
  ["/admin/documents", "المستندات", FileCheck, "documents_manage", "documents"],
  ["/admin/fees", "الرسوم والدفعات", Wallet, "fees_manage", "fees"],
  ["/admin/salaries", "نظام متابعة الرواتب", Banknote, "admin", "salaries"],
  ["/admin/audit", "سجل النشاط", History, "audit_view", "audit"],
  ["/admin/reports", "التقارير والتصدير", BarChart3, "reports_view", "reports"],
  ["/admin/settings", "الإعدادات", Settings, null, "settings"],
];

function GlobalSearch() {
  const [q, setQ] = useState("");
  const dq = useDebounced(q);
  const [r, setR] = useState(null);
  const nav = useNavigate();
  useEffect(() => {
    if (dq.trim().length < 2) { setR(null); return; }
    api.get("/search", { params: { q: dq } }).then((x) => setR(x.data)).catch(() => {});
  }, [dq]);
  const go = (to) => { setQ(""); setR(null); nav(to); };
  const groups = r ? [
    ["السائقون", r.drivers.map((d) => [d.id, `${d.full_name} • ${d.serial}`, `/admin/drivers/${d.id}`])],
    ["المركبات", r.vehicles.map((v) => [v.id, `مركبة ${[v.admin_number, v.plate].filter(Boolean).join("/")}`, `/admin/vehicles?plate=${v.plate}`])],
    ["النقلات", r.trips.map((t) => [t.id, `${t.number} • ${t.car_plate || t.requester_phone}`, `/admin/trips/${t.id}`])],
    ["الفواتير", r.invoices.map((i) => [i.id, `${i.number} • ${i.requester_phone}`, `/admin/invoices/${i.id}`])],
  ].filter((g) => g[1].length) : [];
  return (
    <div className="relative w-full max-w-xl">
      <Search className="w-5 h-5 absolute top-1/2 -translate-y-1/2 right-3 text-slate-400" />
      <input data-testid="global-search-input" value={q} onChange={(e) => setQ(e.target.value)} className="field field-sm pr-10"
        placeholder="بحث سريع: سائق، رقم تسلسلي، هاتف، لوحة، رقم نقلة أو فاتورة" />
      {r && (
        <div className="absolute z-50 top-12 inset-x-0 bg-white border rounded-xl shadow-xl max-h-96 overflow-y-auto" data-testid="global-search-results">
          {groups.length === 0 ? <div className="p-4 text-slate-500">لا توجد نتائج</div> : groups.map(([g, items]) => (
            <div key={g}><div className="px-4 pt-3 text-xs font-bold text-slate-400">{g}</div>
              {items.map(([id, l, to]) => <button key={id} onClick={() => go(to)} className="block w-full text-right px-4 py-2 hover:bg-slate-50">{l}</button>)}</div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminLayout() {
  const { user, logout, can } = useAuth();
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const allowed = ([, , , p]) => !p || (p === "admin" ? user.role === "admin" : can(p));
  const items = NAV.filter(allowed);
  const current = NAV.filter(([to]) => to !== "/admin" && loc.pathname.startsWith(to))[0];
  const blocked = current && !allowed(current);
  const side = (
    <nav className="space-y-1">
      {items.map(([to, label, Icon, , id]) => (
        <NavLink key={to} to={to} end={to === "/admin"} onClick={() => setOpen(false)} data-testid={`admin-nav-${id}`}
          className={({ isActive }) => `flex items-center gap-3 px-4 h-12 rounded-xl font-bold transition-colors ${isActive ? "bg-white text-brand shadow" : "text-white/80 hover:bg-white/10"}`}>
          <Icon className="w-5 h-5" />{label}
        </NavLink>
      ))}
    </nav>
  );
  return (
    <div className="min-h-screen bg-background lg:flex">
      <aside className="no-print hidden lg:flex w-72 bg-brand flex-col justify-between p-4 sticky top-0 h-screen shrink-0 overflow-y-auto">
        <div><div className="px-2 py-4 mb-4 flex flex-col items-center text-center gap-2"><img src="/img/logo.png" alt="شعار المحيط الأطلسي" data-testid="company-logo" className="w-28 h-28 rounded-full bg-white p-1 object-contain shadow-xl ring-4 ring-white/20" />
          <div className="text-white font-black text-lg leading-tight">المحيط الأطلسي</div><div className="text-white/70 text-xs">لنقل البضائع والمعدات</div></div>{side}</div>
        <div className="border-t border-white/15 pt-4 mt-4">
          <div className="text-white font-bold px-2">{user.name}</div>
          <div className="text-white/60 text-sm px-2 mb-2">{user.role === "admin" ? "مدير النظام" : "موظف إدارة"}</div>
          <button data-testid="admin-logout-btn" onClick={logout} className="flex items-center gap-2 text-red-300 px-2 h-10"><LogOut className="w-5 h-5" />تسجيل الخروج</button>
        </div>
      </aside>
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <div className="absolute top-0 bottom-0 right-0 w-72 bg-brand p-4 overflow-y-auto safe-top">
            <div className="flex justify-between items-center mb-4"><Logo light small /><button onClick={() => setOpen(false)} className="text-white"><X className="w-7 h-7" /></button></div>
            {side}
            <button onClick={logout} className="flex items-center gap-2 text-red-300 px-4 h-12 mt-4"><LogOut className="w-5 h-5" />تسجيل الخروج</button>
          </div>
        </div>
      )}
      <div className="flex-1 min-w-0">
        <header className="bg-white border-b sticky top-0 z-30 safe-top no-print">
          <div className="flex items-center gap-3 px-4 lg:px-8 h-16">
            <button data-testid="admin-menu-btn" className="lg:hidden w-11 h-11 rounded-xl bg-brand text-white flex items-center justify-center shrink-0" onClick={() => setOpen(true)}><Menu className="w-6 h-6" /></button>
            <img src="/img/logo.png" alt="" className="lg:hidden w-10 h-10 rounded-full object-contain shrink-0" />
            <GlobalSearch />
            {user.role === "admin" && <PasswordRequests />}
          </div>
        </header>
        <main className="p-4 sm:p-6 lg:p-8 max-w-[1500px]">{blocked
          ? <div className="card p-10 text-center text-lg font-bold text-slate-600" data-testid="no-permission">ليست لديك صلاحية لفتح هذه الصفحة</div>
          : <PageTransition />}</main>
      </div>
    </div>
  );
}
